import { Skeleton } from "@/shared/ui";

interface BoardPageSkeletonProps {
  activeTab: string;
  isSingleProject?: boolean;
}

export default function BoardPageSkeleton({ activeTab, isSingleProject }: BoardPageSkeletonProps) {
  if (activeTab === "overview") {
    return isSingleProject ? <OverviewWorkspaceSkeleton /> : <OverviewGridSkeleton />;
  }

  if (activeTab === "list") {
    return <ListSkeleton />;
  }

  return <KanbanSkeleton />;
}

function OverviewGridSkeleton() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
      {Array.from({ length: 6 }).map((_, i) => (
        <ProjectCardSkeleton key={i} />
      ))}
    </div>
  );
}

function ProjectCardSkeleton() {
  return (
    <div className="flex flex-col radius-lg border surface-base-stroke surface-base-fill overflow-hidden">
      <div className="w-full h-48">
        <Skeleton height={192} borderRadius={0} />
      </div>
      <div className="p-4">
        <Skeleton height={20} width="70%" />
      </div>
    </div>
  );
}

function OverviewWorkspaceSkeleton() {
  return (
    <div className="flex gap-6">
      <div className="flex-1 flex flex-col gap-4 min-w-0">
        <DescriptionSectionSkeleton />
        <MembersSectionSkeleton />
        <GoalsSectionSkeleton />
      </div>
      <div className="w-100 shrink-0">
        <SidebarSkeleton />
      </div>
    </div>
  );
}

function DescriptionSectionSkeleton() {
  return (
    <div className="p-7 radius-3xl surface-base-fill">
      <div className="flex items-center justify-between mb-5">
        <Skeleton width={180} height={20} />
        <Skeleton width={120} height={32} borderRadius={6} />
      </div>
      <Skeleton count={2} height={14} />
    </div>
  );
}

function MembersSectionSkeleton() {
  return (
    <div className="p-7 radius-3xl surface-base-fill">
      <div className="mb-5">
        <Skeleton width={200} height={20} />
      </div>
      <div className="flex flex-wrap gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="flex items-center gap-2">
            <Skeleton width={40} height={40} circle />
            <div className="flex flex-col gap-0.5">
              <Skeleton width={140} height={14} />
              <Skeleton width={100} height={12} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function GoalsSectionSkeleton() {
  return (
    <div className="p-7 radius-3xl surface-base-fill">
      <div className="flex items-center justify-between mb-5">
        <Skeleton width={150} height={20} />
        <Skeleton width={130} height={32} borderRadius={6} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {Array.from({ length: 2 }).map((_, i) => (
          <GoalCardSkeleton key={i} />
        ))}
      </div>
    </div>
  );
}

function GoalCardSkeleton() {
  return (
    <div className="p-4 radius-lg border surface-base-stroke surface-base-fill">
      <Skeleton width="80%" height={16} />
      <div className="mt-10 mb-1 flex items-center justify-between">
        <Skeleton width={40} height={14} />
        <Skeleton width={30} height={12} />
      </div>
      <Skeleton height={8} borderRadius={999} />
      <div className="flex justify-between items-center gap-2 mt-3">
        <div className="flex items-center gap-1.5">
          <Skeleton width={60} height={12} />
          <Skeleton width={80} height={12} />
        </div>
        <Skeleton width={18} height={18} circle />
      </div>
    </div>
  );
}

function SidebarSkeleton() {
  return (
    <div className="p-7 radius-3xl surface-base-fill">
      <Skeleton width={90} height={20} />

      <div className="border-t surface-base-stroke my-4" />

      <div className="flex items-center justify-between mb-5">
        <Skeleton width={80} height={16} />
        <Skeleton width={90} height={14} />
      </div>

      <div className="flex flex-col gap-5">
        {Array.from({ length: 5 }).map((_, i) => (
          <AuditEntrySkeleton key={i} />
        ))}
      </div>
    </div>
  );
}

function AuditEntrySkeleton() {
  return (
    <div className="flex items-start gap-3">
      <Skeleton width={32} height={32} circle />
      <div className="flex-1 flex flex-col gap-1">
        <Skeleton width="90%" height={14} />
        <Skeleton width={100} height={12} />
        <Skeleton width={24} height={24} circle />
      </div>
    </div>
  );
}

function KanbanSkeleton() {
  return (
    <div className="flex flex-col">
      {Array.from({ length: 2 }).map((_, boardIdx) => (
        <KanbanRowSkeleton key={boardIdx} showHeading />
      ))}
    </div>
  );
}

function KanbanRowSkeleton({ showHeading }: { showHeading: boolean }) {
  return (
    <div className="radius-xl p-5 not-last:mb-5 border surface-component-stroke">
      {showHeading && (
        <div className="flex items-center justify-between gap-2 mb-4">
          <Skeleton width={180} height={22} />
          <Skeleton width={20} height={20} borderRadius={4} />
        </div>
      )}

      <div className="overflow-x-auto min-h-[120px]">
        <div className="flex gap-5 pb-2 border-b surface-component-stroke">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="flex-1 min-w-[200px]">
              <div className="flex justify-between items-center gap-1">
                <Skeleton width={12} height={12} />
                <div className="flex items-center gap-1.5">
                  <Skeleton width={80} height={16} />
                  <Skeleton width={26} height={18} borderRadius={4} />
                </div>
                <div className="w-3" />
              </div>
            </div>
          ))}
        </div>

        <div className="flex gap-5 mt-3">
          {Array.from({ length: 4 }).map((_, colIdx) => (
            <div key={colIdx} className="flex-1 flex flex-col gap-2 min-w-[200px]">
              {Array.from({ length: colIdx === 0 ? 3 : colIdx === 1 ? 2 : colIdx === 2 ? 1 : 2 }).map((_, cardIdx) => (
                <KanbanCardSkeleton key={cardIdx} />
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function KanbanCardSkeleton() {
  return (
    <div className="p-3 radius-md border surface-base-stroke surface-base-fill">
      <div className="flex items-start justify-between gap-2 mb-2">
        <Skeleton width="75%" height={14} />
        <Skeleton width={14} height={14} circle />
      </div>
      <div className="flex items-center justify-between gap-2 mt-3">
        <div className="flex items-center gap-1.5">
          <Skeleton width={50} height={12} />
          <Skeleton width={40} height={12} />
        </div>
        <Skeleton width={18} height={18} circle />
      </div>
    </div>
  );
}

function ListSkeleton() {
  return (
    <div className="space-y-6">
      {Array.from({ length: 2 }).map((_, boardIdx) => (
        <ListBoardSkeleton key={boardIdx} />
      ))}
    </div>
  );
}

function ListBoardSkeleton() {
  return (
    <div>
      <div className="flex items-center justify-between gap-2 mb-3">
        <Skeleton width={200} height={22} />
        <Skeleton width={20} height={20} borderRadius={4} />
      </div>

      <div className="radius-lg border surface-base-stroke overflow-hidden">
        <div className="grid grid-cols-[80px_1fr_240px_200px_160px] gap-4 px-3 py-3 border-b surface-base-stroke">
          <Skeleton height={14} />
          <Skeleton height={14} width="40%" />
          <Skeleton height={14} width="50%" />
          <Skeleton height={14} width="50%" />
          <Skeleton height={14} width="50%" />
        </div>

        {Array.from({ length: 4 }).map((_, statusIdx) => (
          <ListStatusGroupSkeleton key={statusIdx} rowCount={statusIdx === 0 ? 3 : statusIdx === 1 ? 2 : 1} />
        ))}
      </div>
    </div>
  );
}

function ListStatusGroupSkeleton({ rowCount }: { rowCount: number }) {
  return (
    <>
      <div className="px-3 pt-4 pb-3 border-b surface-base-stroke">
        <div className="flex justify-between items-center gap-2">
          <div className="flex items-center gap-2">
            <Skeleton width={16} height={16} />
            <Skeleton width={100} height={16} />
            <Skeleton width={40} height={18} borderRadius={4} />
          </div>
          <div className="flex items-center gap-2">
            <Skeleton width={90} height={12} />
            <Skeleton width={6} height={6} circle />
            <Skeleton width={90} height={12} />
          </div>
        </div>
      </div>

      {Array.from({ length: rowCount }).map((_, rowIdx) => (
        <div
          key={rowIdx}
          className="grid grid-cols-[80px_1fr_240px_200px_160px] gap-4 px-3 py-4 border-b surface-base-stroke">
          <Skeleton height={14} width={30} />
          <Skeleton height={14} width="60%" />
          <div className="flex items-center gap-2">
            <Skeleton width={24} height={24} circle />
            <Skeleton height={14} width={120} />
          </div>
          <Skeleton height={14} width={80} />
          <Skeleton height={22} width={70} borderRadius={10} />
        </div>
      ))}
    </>
  );
}
